import { Locale } from '@kamernet/core/Intl/types';

import {
  Route,
  RouteParamsStringifier,
  RouteParamsParser,
  RouteParams,
  RouteParamsString,
} from './types';

function getPathnameRegExp(pathname: string): RegExp {
  return new RegExp(`^${pathname.replaceAll(/\[([^[]+)\]/g, '(?<$1>[^/]*)')}$`);
}

export function defineRoute<
  TRouteParams extends RouteParams = RouteParams,
  TRouteParamsString extends RouteParamsString = RouteParamsString,
>({
  pathname,
  nlPathname,
  routeParamsStringifier,
  routeParamsParser,
  options,
}: {
  pathname: string;
  nlPathname?: string;
  routeParamsStringifier: RouteParamsStringifier<
    TRouteParams,
    TRouteParamsString
  >;
  routeParamsParser: RouteParamsParser<TRouteParamsString, TRouteParams>;
  options: Route['options'];
}): Route<TRouteParams, TRouteParamsString> {
  const pathnameByLocale: Record<Locale, string> = {
    [Locale.en]: pathname,
    [Locale.nl]: nlPathname || pathname,
  };

  const pathnameRegExpByLocale: Record<Locale, RegExp> = {
    [Locale.en]: getPathnameRegExp(pathnameByLocale.en),
    [Locale.nl]: getPathnameRegExp(pathnameByLocale.nl),
  };

  const getURLInternal = (
    routeParamsString: TRouteParamsString,
    locale: Locale,
    localeSerialization?: 'prefix' | 'query' | undefined,
  ) => {
    let url = locale ? pathnameByLocale[locale] || pathname : pathname;
    const urlSearchParams = new URLSearchParams();

    Object.entries(routeParamsString).forEach(([key, val]) => {
      if (!val?.trim()) return;

      if (url.includes(`[${key}]`)) {
        url = url.replace(`[${key}]`, val);
      } else {
        urlSearchParams.append(key, val);
      }
    });

    if (localeSerialization === 'query') {
      urlSearchParams.append('locale', locale);
    }

    const urlSearchParamsString = urlSearchParams.toString();

    return (
      (localeSerialization === 'prefix' && locale !== Locale.nl
        ? `/${locale}`
        : '') +
      url +
      (urlSearchParamsString ? `?${urlSearchParamsString}` : '')
    );
  };

  const route: Route<TRouteParams, TRouteParamsString> = {
    pathname,
    pathnameByLocale,
    pathnameRegExpByLocale,
    options,

    getURL(routeParams, locale, localeSerialization) {
      const routeParamsString = routeParamsStringifier(routeParams, locale);
      return getURLInternal(routeParamsString, locale, localeSerialization);
    },

    stringifyRouteParams(routeParams, locale) {
      return routeParamsStringifier(routeParams, locale);
    },

    parseRouteParams(routeParamsString, locale) {
      return routeParamsParser(routeParamsString as TRouteParamsString, locale);
    },

    getPathnameImplementation(inputPathname: string, locale: Locale) {
      if (
        locale === Locale.en &&
        pathnameRegExpByLocale.en.test(inputPathname)
      ) {
        return inputPathname;
      }

      if (
        locale === Locale.nl &&
        pathnameRegExpByLocale.nl.test(inputPathname)
      ) {
        const routeParamsString =
          pathnameRegExpByLocale.nl.exec(inputPathname)?.groups || {};

        return getURLInternal(
          routeParamsString as TRouteParamsString,
          Locale.en,
        );
      }

      return null;
    },

    getSwitchLanguageURL(routeParamsString, currentLocale) {
      try {
        const rotueParams = this.parseRouteParams(
          routeParamsString,
          currentLocale,
        );

        return this.getURL(
          rotueParams,
          currentLocale === Locale.en ? Locale.nl : Locale.en,
          'prefix',
        );
      } catch {
        return null;
      }
    },
  };

  return Object.freeze(route);
}
